import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAuth, API } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";
import { logger } from "@/lib/logger";

function getTodayLocal() {
  const now = new Date();
  const off = now.getTimezoneOffset();
  const local = new Date(now.getTime() - off * 60000);
  return local.toISOString().slice(0, 10);
}

function pickMimeType() {
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/ogg;codecs=opus",
  ];
  for (const m of candidates) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)) {
      return m;
    }
  }
  return "";
}

export default function RecordPage() {
  const { user } = useAuth();
  const toast = useToast();

  const [date, setDate] = useState(getTodayLocal());
  const [text, setText] = useState("");
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => () => stopStream(), []);

  const stopStream = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const startRecording = async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        toast.show({ variant: "error", title: "Mic not supported", description: "Use a modern browser with HTTPS." });
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = pickMimeType();
      const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];

      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        const blobType = mr.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type: blobType });
        stopStream();
        await transcribeBlob(blob, blobType);
      };
      mr.start();
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch (err) {
      logger.error("Mic permission error:", err);
      toast.show({
        variant: "error",
        title: "Mic permission denied",
        description: "Please allow microphone access in your browser.",
      });
      stopStream();
    }
  };

  const stopRecording = () => {
    try {
      const mr = mediaRecorderRef.current;
      if (mr && mr.state !== "inactive") {
        mr.stop();
      }
    } catch (e) {
      logger.warn("stopRecording error:", e);
    }
    setRecording(false);
  };

  const transcribeBlob = async (blob, mimeType) => {
    setTranscribing(true);
    try {
      const ext = mimeType.includes("mp4") ? "m4a" : mimeType.includes("ogg") ? "ogg" : "webm";
      const fd = new FormData();
      fd.append("file", blob, `recording.${ext}`);
      const { data } = await axios.post(`${API}/transcribe`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setText((prev) => (prev ? prev.trim() + " " + (data.text || "") : data.text || ""));
      toast.show({ variant: "success", title: "Transcribed!", description: "Review and edit before saving." });
    } catch (err) {
      logger.error("Transcription error:", err);
      toast.show({
        variant: "error",
        title: "Transcription failed",
        description: err.response?.data?.detail || "Please try again.",
      });
    } finally {
      setTranscribing(false);
    }
  };

  const saveActivity = async () => {
    if (!text.trim()) {
      return toast.show({ variant: "warning", title: "Nothing to save", description: "Record or type your activity first." });
    }
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/activities`,
        { activity_description: text.trim(), date },
        { withCredentials: true }
      );
      if (data?._offline) {
        toast.show({ variant: "warning", title: "Saved offline", description: "Will auto-sync when you're back online." });
      } else {
        toast.show({ variant: "success", title: "Activity logged!" });
      }
      setText("");
    } catch (err) {
      toast.show({
        variant: "error",
        title: "Save failed",
        description: err.response?.data?.detail || "Try again later.",
      });
    } finally {
      setSaving(false);
    }
  };

  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");
  const busy = recording || transcribing;

  return (
    <div className="px-5 pt-6 max-w-2xl mx-auto" data-testid="record-page">
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">Log Activity</h1>
          <p className="text-slate-400 text-sm mt-1">
            Hi <span className="text-slate-200 font-medium">{user?.name?.split(" ")[0] || "there"}</span> — what did you do today?
          </p>
        </div>
        <div className="w-11 h-11 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-blue-400">
          <i className="fas fa-microphone"></i>
        </div>
      </header>

      {/* Date selector */}
      <div className="bg-slate-800/60 border border-slate-700 rounded-2xl p-4 mb-5">
        <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-2 mb-2">
          <i className="fas fa-calendar-day"></i> Date
        </label>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          data-testid="activity-date-input"
          className="w-full bg-slate-900/70 border border-slate-700 rounded-xl px-4 py-3 text-slate-100 outline-none focus:border-blue-500 transition-colors mono"
        />
      </div>

      {/* Record button */}
      <div className="flex flex-col items-center mb-6">
        <button
          onClick={recording ? stopRecording : startRecording}
          disabled={transcribing}
          data-testid="record-btn"
          className={`relative w-36 h-36 rounded-full flex items-center justify-center text-white text-5xl shadow-2xl btn-press transition-all
            ${recording ? "bg-rose-500 recording-pulse" : "bg-gradient-to-br from-blue-500 to-violet-600 hover:shadow-blue-500/40"}
            disabled:opacity-60`}
        >
          {transcribing ? (
            <span className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full spin"></span>
          ) : (
            <i className={`fas ${recording ? "fa-stop" : "fa-microphone"}`}></i>
          )}
        </button>
        <div className="mt-4 text-center">
          {recording ? (
            <p className="text-rose-300 font-semibold mono" data-testid="record-timer">
              ● {mm}:{ss}
            </p>
          ) : transcribing ? (
            <p className="text-blue-300 font-semibold">Transcribing your audio…</p>
          ) : (
            <p className="text-slate-400 text-sm">Tap to record · or type below</p>
          )}
        </div>
      </div>

      {/* Text area */}
      <div className="bg-slate-800/60 border border-slate-700 rounded-2xl p-4 mb-5">
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-2">
            <i className="fas fa-pen-to-square"></i> Activity description
          </label>
          {text && (
            <button
              onClick={() => setText("")}
              data-testid="clear-text-btn"
              className="text-rose-400 hover:text-rose-300 text-sm"
              aria-label="Clear text"
            >
              <i className="fas fa-circle-xmark"></i>
            </button>
          )}
        </div>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Record your voice or type what you did today…"
          rows={6}
          data-testid="activity-text-input"
          disabled={busy}
          className="w-full bg-slate-900/70 border border-slate-700 rounded-xl px-4 py-3 text-slate-100 placeholder-slate-500 outline-none focus:border-blue-500 transition-colors resize-y disabled:opacity-70"
        />
        <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
          <span>{text.length} chars</span>
          <span>Tip: edit before saving</span>
        </div>
      </div>

      {/* Save */}
      <button
        onClick={saveActivity}
        disabled={!text.trim() || saving}
        data-testid="save-activity-btn"
        className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 disabled:text-slate-400 text-white font-semibold py-3.5 rounded-2xl btn-press flex items-center justify-center gap-2"
      >
        {saving ? (
          <span className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full spin"></span>
        ) : (
          <>
            <i className="fas fa-circle-check"></i> Save activity
          </>
        )}
      </button>
    </div>
  );
}
